/**
 * MONSTER TOWN — THE COLLECTION BOOK. FISHING/BUGS CREW (src/ui/collection*).
 *
 * Expansion brief §6: "A collection book: a page per creature with its picture,
 * filling up as you find them. Big art, no reading required. Completion is its
 * own reward plus coins."
 *
 * THE BOOK IS THE REWARD, SO THE BOOK HAS TO BE A PICTURE. Every creature here
 * is drawn by its own painter (`CollectionEntry.draw`, the hook the frozen
 * contract put there for exactly this) at 100+ px. There is not one word on any
 * screen this file produces — not a name, not a count, not a "3/10". Counts are
 * COUNTABLE DOTS, the page's progress is one circle per creature, and when a
 * creature is out is four pictures of the sky with the right ones lit up.
 *
 * WHY IT IS NOT A `PanelHost` PANEL. The panel host lives inside `installHud`
 * and is not reachable from another crew, and crews do not import each other.
 * So the book is its own layer and the two of them stay out of each other's way
 * over the bus: opening the book asks any open panel to close (`ui:panel: null`)
 * and any panel opening closes the book. It keeps every rule the panel host
 * exists to keep, and the important one is structural here too — THE WAY OUT IS
 * BUILT BEFORE THE WAY IN. The 112 px back button is the first child appended,
 * before the page has been chosen, so there is no code path that can produce a
 * book a three-year-old cannot get out of.
 *
 * TABS APPEAR AS CONTENT LANDS. `recipe` and `animal` are two of the four kinds
 * in `core/collection.ts`; the cooking and zoo crews own them. A kind with no
 * entries registered has no tab, so this file needs no change on the day they
 * land — and a kid never opens an empty page and thinks the book is broken.
 */

import './collection.css'
import { clock } from '../core/time'
import { save } from '../core/save'
import {
  collection,
  collectiblesOfKind,
  isAvailable,
  type CollectionEntry,
  type CollectionKind,
} from '../core/collection'
import { bus } from '../core/events'
import type { GameContext } from '../app/context'
import { css, type PaletteName } from '../core/palette'
import {
  bookIcon,
  bookButtonIcon,
  netIcon,
  pawIcon,
  phasePip,
  placePic,
  potIcon,
  rodIcon,
  silhouette,
  unknownIcon,
} from '../catching/bookArt'
import { paintEl, type Painter } from '../catching/paint'
import { button, el, onTap } from './dom'
import { iconEl } from './icons'

/**
 * The order pages appear in, the picture on each tab — and THE PAGE'S OWN
 * HABITAT, which is what turns a grid of cards into a page of a book.
 *
 * Round `vr2`: "there is no book: no spine, no page edge, no paper, no shelf, no
 * habitat — just a grid on beige with large dead bands above and below."
 *
 * The two colours below are painted behind the cards as sky-over-ground, so the
 * bug page is a meadow and the fish page is a pond. They are PALETTE NAMES read
 * through `css()` and handed to CSS as custom properties, never hexes typed into
 * the stylesheet: this crew colour-matches the world, and a stylesheet that
 * hard-codes `#6FBF57` is the exact drift `palette.ts` opens by forbidding.
 * A new page kind is one row here.
 */
const KINDS: readonly {
  kind: CollectionKind
  icon: Painter
  sky: PaletteName
  ground: PaletteName
}[] = [
  { kind: 'bug', icon: netIcon, sky: 'skyHorizon', ground: 'grassLight' },
  { kind: 'fish', icon: rodIcon, sky: 'glass', ground: 'water' },
  { kind: 'recipe', icon: potIcon, sky: 'wallpaperCream', ground: 'path' },
  { kind: 'animal', icon: pawIcon, sky: 'wallMint', ground: 'sand' },
]

function habitatOf(kind: CollectionKind): { sky: PaletteName; ground: PaletteName } {
  const row = KINDS.find((k) => k.kind === kind)
  return { sky: row?.sky ?? 'skyHorizon', ground: row?.ground ?? 'grassLight' }
}

/** Representative hours for the four bands, for the "when is it out" pips. */
const BAND_HOURS: readonly { id: string; hour: number }[] = [
  { id: 'dawn', hour: 6 },
  { id: 'day', hour: 12 },
  { id: 'dusk', hour: 18.5 },
  { id: 'night', hour: 23 },
]

/** Every place tag this crew can put a creature in. */
const PLACES = ['grass', 'garden', 'tree', 'pond'] as const

let layer: HTMLElement | null = null
let root: HTMLElement | null = null
let currentKind: CollectionKind = 'bug'
let openCard: string | null = null
let foundCard: HTMLElement | null = null
let foundTimer = 0
let installed = false
/**
 * THE BOOK OPENS ON WHAT YOU JUST CAUGHT.
 *
 * Round `vr1`: "after landing a fish, the book opened on the BUG tab: ten cards,
 * every one a '?'. A three-year-old's reward for their first ever catch is a
 * wall of ten question marks." `currentKind` starts at 'bug' and only ever moved
 * when somebody tapped a tab, so the one page a kid had a reason to see was the
 * one page they were never shown.
 *
 * A find sets this; tapping a tab CLEARS it, because a tap is a choice and a
 * choice outranks a guess. So: catch a fish, open the book, see the fish page —
 * but a kid who has deliberately gone to the bug page and closed the book still
 * comes back to the bug page.
 */
let lastFoundKind: CollectionKind | null = null
/** QA only: hold the "you got one!" card up so a tool can photograph it. */
let foundHoldMs = 0

// ---------------------------------------------------------------------------
// small pieces
// ---------------------------------------------------------------------------

/**
 * The creature's own picture — and if it has not been found yet, ITS OWN
 * SILHOUETTE. Never a question mark: see the long note on `silhouette` in
 * `bookArt.ts`. A creature from a crew that has no painter (a recipe, an animal)
 * falls back to an empty net, which is the same sentence in the same vocabulary.
 */
function creatureArt(entry: CollectionEntry, size: number, found: boolean): HTMLElement {
  if (entry.draw === undefined) {
    // A kind this crew does not own may only have an icon.
    return found ? iconEl(entry.icon, size) : paintEl(unknownIcon, size)
  }
  if (!found) return paintEl(silhouette(entry.id, entry.draw), size)
  return paintEl(entry.draw, size, entry.devName)
}

/**
 * How many you have caught, as dots you can count — on a CHIP with the page's
 * own tool on it. Round `vr2`: "a tiny unexplained gold dot between them". It
 * was one 18 px pip floating in the middle of a cream void with nothing to say
 * what it counted. A net and one dot is a sentence a pre-reader can finish.
 * Capped at six so it never wraps.
 */
function countPips(n: number, kind?: CollectionKind): HTMLElement {
  const row = el('span', 'mt-book-count')
  if (kind !== undefined) {
    const tool = KINDS.find((k) => k.kind === kind)?.icon
    if (tool !== undefined) row.append(paintEl(tool, 30))
  }
  for (let i = 0; i < Math.min(n, 6); i++) row.append(el('span', 'mt-book-pip'))
  return row
}

/**
 * HOW BIG A CARD, AND HOW MANY ACROSS.
 *
 * MEASURED, not guessed off a bucket table. The first version hard-coded 132 px
 * for a ten-card page and the captured frame showed ten small cards huddled in
 * the top third of an iPad with two thirds of the page empty below them — and
 * an 8+2 wrap, which reads as "the last two are different" rather than as one
 * page of ten. It now tries every column count and keeps the one that makes the
 * biggest square, with a balanced last row.
 *
 * The box comes from the layer's OWN bounding rect. Never `window.innerWidth`:
 * the hard rule in this repo is about the canvas rect versus the window, and a
 * HUD that lays itself out against a number the browser disagrees with is the
 * same defect wearing a different hat.
 */
function gridPlan(n: number, box: DOMRect): { size: number; cols: number; cardH: number } {
  const GAP = 12
  // The binding down the left (48) and the page edges on the right (16) come off
  // the width; the header (96 + gap) and the progress row (~40) off the height.
  const availW = Math.max(200, box.width - 32 - 48 - 16 - 24)
  const availH = Math.max(180, box.height - 210)
  let best = { size: 88, cols: Math.max(1, Math.ceil(Math.sqrt(n))) }
  for (let cols = 1; cols <= n; cols++) {
    const rows = Math.ceil(n / cols)
    const w = Math.floor((availW - GAP * (cols - 1)) / cols)
    const h = Math.floor((availH - GAP * (rows - 1)) / rows)
    const size = Math.min(w, h, 260)
    if (size >= 88 && size > best.size) best = { size, cols }
  }
  /**
   * A CARD MAY BE TALLER THAN IT IS WIDE, and on an iPad in landscape it always
   * is. Ten square cards five across are limited by the WIDTH, and the height
   * they leave over was the "large dead bands above and below" the critic
   * measured — the page had the room and the layout refused to use it. The card
   * takes up to another 40% in height (never more, or a card stops reading as a
   * card), and the picture inside it is still sized off the square so a wider
   * page cannot blow the HUD's oversized-picture gate.
   */
  const rows = Math.ceil(n / best.cols)
  const cardH = Math.max(
    best.size,
    Math.min(Math.floor((availH - GAP * (rows - 1)) / rows), Math.round(best.size * 1.4)),
  )
  return { ...best, cardH }
}

// ---------------------------------------------------------------------------
// one creature's page
// ---------------------------------------------------------------------------

/**
 * ONE CREATURE'S PAGE.
 *
 * Round `vr2`: "the single-creature page is 85% empty: one fish, four amber time
 * chips and a water chip floating in a cream void, with a tiny unexplained gold
 * dot between them."
 *
 * That was three separate holes and it needed all three filled. The picture had
 * nothing to stand ON (so it floated); the page had nothing to fill the two
 * thirds of an iPad the picture is not allowed to use; and the four time chips
 * were all lit, which is not a bug — that fish really is out all day — but a row
 * where every cell looks the same teaches nothing about the one thing this page
 * exists to teach.
 *
 * So the creature now stands in a HABITAT: a framed scene, the page's own sky
 * over the page's own ground, which grows to fill whatever height is left. And
 * the WHEN row's off state is unmistakable rather than a change of opacity —
 * grey, flat, smaller and unlit beside a gold, raised, lit one.
 */
function buildPage(entry: CollectionEntry, back: () => void): HTMLElement {
  const page = el('div', 'mt-book-page')
  /**
   * "BIG ART" (brief §6) — but the HUD crew's oversized-widget gate fails any
   * picture over 30% of the canvas on either axis, and it is right to: that
   * gate exists because an unsized icon once filled the middle of the game.
   * 26% of the SHORT side is as big as a page picture is allowed to be, and on
   * a 1024x768 iPad that is a 200 px creature. The habitat frame around it is a
   * `div` with a gradient, which the gate does not measure and which is how the
   * page fills its height without breaking the rule.
   */
  const box = (layer ?? page).getBoundingClientRect()
  const art = Math.round(Math.max(140, Math.min(240, Math.min(box.width, box.height) * 0.28)))
  const stage = el('div', 'mt-book-stage')
  stage.append(creatureArt(entry, art, true))
  const tally = el('div', 'mt-book-tally')
  tally.append(countPips(collection.count(entry.id), entry.kind))
  stage.append(tally)
  page.append(stage)

  const facts = el('div', 'mt-book-facts')

  // WHEN. Four skies; the ones this creature is out in are lit.
  const when = el('div', 'mt-book-when')
  const day = clock.day
  for (const band of BAND_HOURS) {
    const cell = el('span', '')
    let on = false
    for (const place of PLACES) {
      if (
        isAvailable(entry.id, {
          hour: band.hour,
          phase: band.id as 'dawn' | 'day' | 'dusk' | 'night',
          day,
          place,
        })
      ) {
        on = true
        break
      }
    }
    cell.dataset['on'] = String(on)
    const pip = phasePip[band.id]
    if (pip !== undefined) cell.append(paintEl(pip, 56))
    when.append(cell)
  }
  facts.append(when)

  // WHERE. Only the places it actually turns up in, so the row is the answer.
  const where = el('div', 'mt-book-where')
  const hours = [3, 6, 9, 12, 15, 18, 21]
  for (const place of PLACES) {
    const ok = hours.some((hour) =>
      isAvailable(entry.id, {
        hour,
        phase: hour < 5 || hour >= 20 ? 'night' : hour < 7 ? 'dawn' : hour < 17 ? 'day' : 'dusk',
        day,
        place,
      }),
    )
    if (!ok) continue
    const cell = el('span', '')
    const pic = placePic[place]
    if (pic !== undefined) cell.append(paintEl(pic, 56))
    where.append(cell)
  }
  if (where.childElementCount > 0) {
    facts.append(el('span', 'mt-book-facts-gap'))
    facts.append(where)
  }
  page.append(facts)

  onTap(page, back, 'paper')
  return page
}

// ---------------------------------------------------------------------------
// the book
// ---------------------------------------------------------------------------

function paint(): void {
  if (root === null) return
  root.replaceChildren()

  // ---- the way OUT, built first --------------------------------------------
  const head = el('div', 'mt-book-head')
  head.append(
    button({ icon: 'back', label: 'Back', className: 'big paper', cue: 'ui_back' }, () => {
      if (openCard !== null) {
        openCard = null
        paint()
        return
      }
      closeBook()
    }),
  )
  head.append(paintEl(bookIcon, 64))

  const kinds = KINDS.filter((k) => collectiblesOfKind(k.kind).length > 0)
  if (kinds.every((k) => k.kind !== currentKind)) {
    currentKind = kinds[0]?.kind ?? 'bug'
  }
  const tabs = el('div', 'mt-book-tabs')
  for (const k of kinds) {
    const b = button({ label: `${k.kind} page`, className: 'mt-book-tab', cue: 'paper' }, () => {
      currentKind = k.kind
      // A tap is a CHOICE, and a choice outranks "the page of your last catch".
      lastFoundKind = null
      openCard = null
      paint()
    })
    b.dataset['on'] = String(k.kind === currentKind)
    b.append(paintEl(k.icon, 44))
    tabs.append(b)
  }
  head.append(tabs)
  root.append(head)

  const rows = collection.pageOf(currentKind)

  /**
   * ---- THE BOOK ITSELF ------------------------------------------------------
   *
   * Round `vr2`: "there is no book: no spine, no page edge, no paper, no shelf,
   * no habitat — just a grid on beige."
   *
   * Everything that answers that lives on this one element and its two children:
   * a bound left edge with stitching, a stack of page edges down the right, real
   * paper with a ruled grain, and the page's own habitat behind the cards. It is
   * `flex: 1`, so the "large dead bands above and below" become the page a kid is
   * looking at rather than the colour of the panel behind it.
   *
   * The two habitat colours come from the palette through `css()` (see `KINDS`),
   * so nothing here is a hex literal and the pond in the book is the pond in the
   * town.
   */
  const spread = el('div', 'mt-book-spread')
  const habitat = habitatOf(currentKind)
  spread.style.setProperty('--sky', css(habitat.sky))
  spread.style.setProperty('--ground', css(habitat.ground))
  spread.dataset['kind'] = currentKind
  spread.append(el('div', 'mt-book-spine'))
  spread.append(el('div', 'mt-book-edges'))

  // ---- one creature, or the grid -------------------------------------------
  //
  // `openCard` IS CLEARED HERE, not only where it is set. Changing page while a
  // creature's page was open used to leave the id pointing at a creature on
  // another page: the grid drew correctly, but BACK then thought it was closing
  // a detail view and only repainted — so the one button that must always get a
  // kid out of a screen did nothing at all. The view and the flag are decided in
  // the same place now, which is the only way they cannot disagree.
  const open = openCard === null ? null : (rows.find((r) => r.entry.id === openCard && r.found) ?? null)
  if (open === null) openCard = null
  if (open !== null) {
    spread.append(
      buildPage(open.entry, () => {
        openCard = null
        paint()
      }),
    )
    root.append(spread)
  } else {
    const body = el('div', 'mt-book-body')
    const plan = gridPlan(rows.length, (layer ?? root).getBoundingClientRect())
    const size = plan.size
    body.style.setProperty('--card', `${size}px`)
    body.style.setProperty('--cardh', `${plan.cardH}px`)
    body.style.setProperty('--cols', String(plan.cols))
    for (const row of rows) {
      const card = el('div', 'mt-book-card')
      card.dataset['found'] = String(row.found)
      card.setAttribute('role', 'button')
      card.setAttribute('aria-label', row.found ? row.entry.devName : 'Not found yet')
      // Sized off BOTH the card's width and its height: a tall card with a
      // width-sized picture in the top of it is most of a card of nothing.
      card.append(
        creatureArt(row.entry, Math.round(Math.min(size, plan.cardH * 0.72) * 0.78), row.found),
      )
      card.append(countPips(row.count))
      onTap(
        card,
        () => {
          // An unfound card is NOT a dead tap (LAW #1 in spirit): it wiggles and
          // chirps, which is the only honest answer to "what is this one?".
          if (!row.found) {
            card.dataset['press'] = '1'
            window.setTimeout(() => delete card.dataset['press'], 160)
            bus.emit('audio:cue', { cue: 'nudge' })
            return
          }
          openCard = row.entry.id
          paint()
        },
        'paper',
      )
      body.append(card)
    }
    spread.append(body)

    // ---- the page's own progress, printed at the foot of the page ------------
    const foot = el('div', 'mt-book-foot')
    for (const row of rows) {
      const p = el('span', 'mt-book-prog')
      p.dataset['on'] = String(row.found)
      foot.append(p)
    }
    spread.append(foot)
    root.append(spread)
  }
}

/** The book's own name on the `ui:panel` wire. */
export const BOOK_PANEL = 'book'
/**
 * ==========================================================================
 *  SAYING `null` IS NOT THE SAME AS SAYING "I AM OPEN" (fixed vr2)
 * ==========================================================================
 * Opening the book used to emit `ui:panel { panel: null }` — "everybody put
 * yourselves away". Every sheet built on `PanelHost` heard that correctly, but a
 * sheet that lives in its OWN layer cannot: `null` is exactly what a sheet says
 * when it closes, so acting on it would make any close close everything. The
 * zoo's own listener therefore ignores `null`, and the book and the feeding
 * sheet could genuinely be up at once, both at z-index 31 — measured, with
 * `elementsFromPoint` returning a book card sitting on the zoo's card grid.
 * `null` also left `#hud[data-panel]` at '0', so the coin purse and the whole
 * left rail were drawn over a full-screen book.
 *
 * So the book announces itself by NAME, exactly the way the kitchen does (see
 * `announceOpen` in `src/cooking/sheet.ts`, whose comment explains the two
 * emits: the first makes the other sheets close, and a sheet closing answers
 * with `null`, which would undo the HUD flip; the second lands after they have
 * all gone quiet and is a no-op). Our own listener ignores both.
 */
let announcing = false
function announceOpen(): void {
  if (announcing) return
  announcing = true
  try {
    bus.emit('ui:panel', { panel: BOOK_PANEL })
    bus.emit('ui:panel', { panel: BOOK_PANEL })
  } finally {
    announcing = false
  }
}

export function openBook(kind?: CollectionKind): void {
  if (layer === null) return
  // No page asked for? Then the page a kid has a reason to see is the one their
  // last find went on. See `lastFoundKind`.
  const want = kind ?? lastFoundKind ?? currentKind
  if (want !== currentKind && collectiblesOfKind(want).length > 0) {
    currentKind = want
    openCard = null
  }
  if (root !== null) {
    paint()
    return
  }
  // Any full-screen panel gets out of the way — never two layers deep.
  announceOpen()
  root = el('div', 'mt-book')
  layer.append(root)
  openCard = null
  paint()
  clock.pause('book')
  bus.emit('audio:cue', { cue: 'ui_open' })
}

export function closeBook(): void {
  if (root === null) return
  root.remove()
  root = null
  openCard = null
  clock.resume('book')
  bus.emit('audio:cue', { cue: 'ui_close' })
  // ...AND SAY SO. Now that opening announces a panel by name, closing has to
  // hand the HUD back or `#hud[data-panel]` stays at '1' for ever and the kid's
  // coin purse, rail and camera buttons never come back. Same pair as the
  // kitchen's.
  bus.emit('ui:panel', { panel: null })
}

export function bookIsOpen(): boolean {
  return root !== null
}

// ---------------------------------------------------------------------------
// the discovery moment
// ---------------------------------------------------------------------------

/**
 * "YOU GOT ONE!" — a big picture of the thing you just caught, in the middle of
 * the screen. It is not a control and it is not in the way: `pointer-events:
 * none`, and it takes itself down.
 *
 * ===========================================================================
 *  HOW LONG, AND WHY IT IS NOT 1.9 SECONDS ANY MORE
 * ===========================================================================
 * Round `vr1` reported the card as never reaching the glass at all: a probe
 * polled `foundCardUp()`, got TRUE, screenshotted, and the frame had no card in
 * it. That was measured properly before anything here was changed, with a
 * MutationObserver inside the page (`tools/fix-catch-r1-probe.mjs`):
 *
 *     add 5 ms · leaving 2199 ms · gone 2948 ms   — the card was real
 *     one page.screenshot() on this harness       — 2763 ms
 *
 * So the card WAS on screen and the shutter was slower than the card. That is a
 * tooling defect, not a rendering one — but "the only celebration in the game
 * cannot be photographed by the repo's own tools" is still a real problem,
 * because a moment no gate can see is a moment that regresses in silence.
 *
 * Two answers, and it needs both. The card is now a proper BEAT rather than a
 * blink — a burst behind it, a bigger picture, and long enough that a
 * three-year-old who was looking at their monster still catches it. And
 * `__book.holdFound(ms)` lets a tool pin it, the same way the time crew's
 * `__time.holdSleep(ms)` pins the bed fade for exactly this reason.
 */
const FOUND_MS = 3200
const FOUND_AGAIN_MS = 2100

function showFound(entry: CollectionEntry, firstTime: boolean): void {
  if (layer === null) return
  foundCard?.remove()
  if (foundTimer !== 0) window.clearTimeout(foundTimer)
  /**
   * ONE WRAPPER, so the burst and the card can never get separated — every
   * removal path in this file takes down `foundCard` and nothing else, and a
   * gold sunburst left spinning over a kid's town on its own would be a bug
   * nobody would think to look for.
   */
  const wrap = el('div', 'mt-book-flash')
  // The burst is BEHIND the card, not inside it: a negative z-index child still
  // paints over its own parent's background, which put gold rays across the
  // card face instead of around it.
  wrap.append(el('div', 'mt-book-burst'))
  const card = el('div', 'mt-book-found')
  card.dataset['new'] = String(firstTime)
  card.append(creatureArt(entry, 176, true))
  card.append(countPips(collection.count(entry.id)))
  wrap.append(card)
  layer.append(wrap)
  foundCard = wrap
  const hold = foundHoldMs > 0 ? foundHoldMs : firstTime ? FOUND_MS : FOUND_AGAIN_MS
  foundTimer = window.setTimeout(() => {
    foundTimer = 0
    wrap.dataset['leaving'] = '1'
    window.setTimeout(() => {
      wrap.remove()
      if (foundCard === wrap) foundCard = null
    }, 320)
  }, hold)
}

// ---------------------------------------------------------------------------
// install
// ---------------------------------------------------------------------------

/**
 * THE BUTTON GOES IN THE RAIL, IN THE SEVENTH SLOT.
 *
 * The rail is a fixed 3x2 grid and the HUD crew's rule is that no control in it
 * ever changes position (a pre-reader navigates by place). Appending a seventh
 * child to a 3-column grid keeps all six where they are and starts a third row,
 * so the rule holds. If the rail is not there at all — a HUD that has not
 * mounted, a future layout — the button falls back to its own corner box rather
 * than disappearing, the same "neither crew can break the other by shipping
 * first" pattern the undo button uses.
 */
function mountButton(hud: HTMLElement): HTMLElement {
  const b = button({ label: 'My collection book', cue: 'ui_tap' }, () => {
    if (bookIsOpen()) closeBook()
    else openBook()
  })
  // A BOOK, not a net — see the note on `bookButtonIcon`. The net was the only
  // monochrome glyph in the rail and was the same silhouette as the two zoom
  // magnifiers in the opposite corner.
  b.append(paintEl(bookButtonIcon, 52))
  const rail = hud.querySelector('.mt-leftstack')
  if (rail !== null) {
    rail.append(b)
    return b
  }
  const fallback = el('div', 'mt-row')
  fallback.style.position = 'absolute'
  fallback.style.left = 'calc(var(--safe-l) + var(--pad))'
  fallback.style.bottom = 'calc(var(--safe-b) + var(--pad) + 110px)'
  fallback.append(b)
  hud.append(fallback)
  return b
}

export function installCollectionBook(ctx: GameContext): void {
  if (installed) return
  installed = true

  layer = el('div', 'mt-book-layer')
  ctx.hud.append(layer)

  let btn: HTMLElement | null = null
  // The HUD calls `hud.replaceChildren()` in its own install, so mount after a
  // microtask and re-mount if anything ever takes the rail away underneath us.
  const remount = (): void => {
    if (btn !== null && btn.isConnected) return
    if (layer !== null && !layer.isConnected) ctx.hud.append(layer)
    btn = mountButton(ctx.hud)
  }
  queueMicrotask(remount)
  bus.on('app:phaseChanged', remount)

  bus.on('collection:found', (e) => {
    /**
     * A DISCOVERY IS ALWAYS WORTH A WRITE. `core/collection.ts` deliberately
     * does not touch the save layer — it is a pure registry — so whoever calls
     * `found()` owes the save request. This crew's own catches do it in
     * `reward.ts`, but a recipe learned in the kitchen or an animal met at the
     * zoo comes through the same event, and a page a kid filled in must not
     * depend on some other crew having remembered. One idempotent, debounced
     * call; the save layer coalesces it.
     */
    save.request('collection:found')
    const entry = collectiblesOfKind(e.kind).find((x) => x.id === e.id)
    if (entry === undefined) return
    lastFoundKind = e.kind
    showFound(entry, e.firstTime)
    // The book is open and the page just changed — repaint it under them.
    if (root !== null) paint()
  })

  // One layer at a time, both ways.
  bus.on('ui:panel', (e) => {
    if (announcing) return // our own "I am open" — see `announceOpen()`
    if (e.panel !== null) closeBook()
  })
  bus.on('app:modeChanged', () => closeBook())
  bus.on('scene:transitionStart', () => closeBook())
  bus.on('save:loaded', () => {
    closeBook()
    foundCard?.remove()
    foundCard = null
  })

  window.__book = {
    ready: true,
    open: (kind) => openBook(kind as CollectionKind | undefined),
    close: () => closeBook(),
    isOpen: () => bookIsOpen(),
    kind: () => currentKind,
    cards: () => (root === null ? 0 : root.querySelectorAll('.mt-book-card').length),
    tabs: () => (root === null ? 0 : root.querySelectorAll('.mt-book-tab').length),
    ids: (kind) =>
      collectiblesOfKind((kind ?? currentKind) as CollectionKind).map((e) => e.id),
    openCard: () => openCard,
    tapCard: (id) => {
      if (root === null) return false
      const rows = collection.pageOf(currentKind)
      const i = rows.findIndex((r) => r.entry.id === id)
      const nodes = root.querySelectorAll<HTMLElement>('.mt-book-card')
      const node = i < 0 ? null : nodes[i]
      if (node === null || node === undefined) return false
      node.dispatchEvent(new PointerEvent('pointerdown', { bubbles: true, pointerId: 1 }))
      node.dispatchEvent(new PointerEvent('pointerup', { bubbles: true, pointerId: 1 }))
      return true
    },
    foundCardUp: () => foundCard !== null && foundCard.isConnected,
    /**
     * QA ONLY, and there is no kid-reachable path to it. A `page.screenshot()`
     * on this repo's harness measures 2.7 s; the celebration is 3.2. Without a
     * hold, whether a tool photographs it depends on how busy the build box is,
     * which is how round `vr1` came to report a card that was really there as
     * missing. Pass 0 to put it back to normal.
     */
    holdFound: (ms) => {
      foundHoldMs = Math.max(0, ms)
    },
    foundPicture: () =>
      foundCard === null ? null : (foundCard.querySelector('canvas') !== null),
    words: () => (root === null ? '' : (root.textContent ?? '').trim()),
    pageProgress: () => {
      const c = collection.completion(currentKind)
      return { found: c.found, total: c.total }
    },
  }

  // Timer bookkeeping only; nothing here can leave a card on screen for ever.
  bus.on('app:phaseChanged', (e) => {
    if (e.phase === 'photo' && foundTimer !== 0) {
      foundCard?.remove()
      foundCard = null
    }
  })
}

export interface BookTestApi {
  readonly ready: boolean
  open(kind?: string): void
  close(): void
  isOpen(): boolean
  kind(): string
  cards(): number
  tabs(): number
  /** Every id on a page, in book order. Tools only. */
  ids(kind?: string): string[]
  openCard(): string | null
  tapCard(id: string): boolean
  foundCardUp(): boolean
  /** Pin the "you got one!" card so a slow shutter can catch it. Tools only. */
  holdFound(ms: number): void
  /** Is there a real drawn creature on the card, or an empty box? Tools only. */
  foundPicture(): boolean | null
  /** LAW #4 — must always be ''. There is no text in this book. */
  words(): string
  pageProgress(): { found: number; total: number }
}

declare global {
  interface Window {
    __book?: BookTestApi
  }
}
